import type { HTMLAttributes, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import { mergeChartEvents } from '@/components/Chart/hooks/options/useChartFunctions';
import type { ChartRef, ExtendedOptions } from '@/components/Chart/types/highcharts';
import { Highcharts } from '@/lib/highcharts';

import type HighchartsType from 'highcharts';

type ChartShellProps = {
  options: ExtendedOptions | undefined;
  isStockChart?: boolean;
  chartId?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  chartComponentRef?: RefObject<ChartRef | null>;
  onChartReady?: (chart: HighchartsType.Chart) => void;
};

const ChartShell = ({
  options,
  isStockChart = false,
  chartId,
  containerProps,
  chartComponentRef,
  onChartReady
}: ChartShellProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const internalRef = useRef<ChartRef | null>(null);
  const chartRefToUse = chartComponentRef ?? internalRef;
  const { chartRef } = useChartContext();

  const handleChartReady = useCallback(
    function (this: HighchartsType.Chart) {
      const renderer = (this as Partial<HighchartsType.Chart>).renderer;
      if (renderer?.forExport) return;

      chartRefToUse.current = { chart: this };
      chartRef.current = { chart: this };
      if (onChartReady) onChartReady(this);
    },
    [chartRef, chartRefToUse, onChartReady]
  );

  const mergedOptions = useMemo(() => {
    if (!options) return options;
    return {
      ...options,
      chart: {
        ...options.chart,
        events: mergeChartEvents(options.chart?.events, { load: handleChartReady })
      }
    };
  }, [options, handleChartReady]);

  // Mount: create chart imperatively
  useEffect(() => {
    if (!containerRef.current || !mergedOptions) return;

    const createFn = isStockChart ? Highcharts.stockChart : Highcharts.chart;
    const chart = createFn(containerRef.current, mergedOptions as any);

    chartRefToUse.current = { chart };
    chartRef.current = { chart };

    return () => {
      chart.destroy();
      chartRefToUse.current = null;
      chartRef.current = null;
    };
    // Only run on mount/unmount and when chartId changes (key-like behavior)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, isStockChart]);

  // Update: apply option changes without recreating the chart
  const prevOptionsRef = useRef(mergedOptions);
  useEffect(() => {
    if (!chartRefToUse.current || prevOptionsRef.current === mergedOptions) return;
    prevOptionsRef.current = mergedOptions;

    if (!mergedOptions) return;

    const chart = chartRefToUse.current.chart;
    if (!(chart as any).renderer?.forExport) {
      // Strip rangeSelector.selected to preserve the user's current range
      const { rangeSelector, ...rest } = mergedOptions;
      const updateOptions = rangeSelector
        ? { ...rest, rangeSelector: { ...rangeSelector, selected: undefined } }
        : rest;

      chart.update(updateOptions as any, true, true);
    }
  }, [mergedOptions, chartRefToUse]);

  if (!mergedOptions) return null;

  return (
    <div
      ref={containerRef}
      {...containerProps}
    />
  );
};

export default ChartShell;
